import { useLayoutEffect, useRef, useState } from 'react';

type WorkCaptionMeasureText = {
  artistName: string;
  title: string;
  inlineInfo: string;
};

let measureCanvas: HTMLCanvasElement | null = null;

const getTextWidth = (
  context: CanvasRenderingContext2D,
  text: string,
  style: CSSStyleDeclaration,
  fontStyle: string,
) => {
  context.font = `${fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  return context.measureText(text).width;
};

const measureCaptionWidth = (
  styleElement: HTMLDivElement,
  { artistName, title, inlineInfo }: WorkCaptionMeasureText,
) => {
  measureCanvas ??= document.createElement('canvas');
  const context = measureCanvas.getContext('2d');
  if (!context) return 0;

  const style = window.getComputedStyle(styleElement);
  const prefix = artistName ? `${artistName}, ` : '';
  const suffix = `, ${inlineInfo}`;
  const text = prefix + title + suffix;
  const letterSpacing = Number.parseFloat(style.letterSpacing) || 0;
  const wordSpacing = Number.parseFloat(style.wordSpacing) || 0;
  const spacingWidth = Math.max(0, Array.from(text).length - 1) * letterSpacing
    + (text.match(/\s/g)?.length ?? 0) * wordSpacing;

  return getTextWidth(context, prefix, style, style.fontStyle)
    + getTextWidth(context, title, style, 'oblique')
    + getTextWidth(context, suffix, style, style.fontStyle)
    + spacingWidth
    + 2;
};

export const useShouldStackWorkCaption = ({
  artistName,
  title,
  inlineInfo,
}: WorkCaptionMeasureText) => {
  const availableRef = useRef<HTMLDivElement>(null);
  const textStyleRef = useRef<HTMLDivElement>(null);
  const [shouldStack, setShouldStack] = useState(false);

  useLayoutEffect(() => {
    const available = availableRef.current;
    const styleElement = textStyleRef.current;
    if (!available || !styleElement) return;

    const measure = () => {
      const availableWidth = available.clientWidth;
      if (availableWidth <= 0) return;
      setShouldStack(measureCaptionWidth(styleElement, {
        artistName,
        title,
        inlineInfo,
      }) > availableWidth);
    };

    let active = true;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(available);
    document.fonts?.ready.then(() => {
      if (active) measure();
    });
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [artistName, title, inlineInfo]);

  return { availableRef, textStyleRef, shouldStack };
};
